import { useEffect, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { DollarSign, TrendingUp, Wallet, CreditCard, Send, Building2, ArrowRightLeft } from "lucide-react";
import { toast } from "sonner";

export default function PayoutsPage() {
  const { user } = useAuth();
  const [payouts, setPayouts] = useState([]);
  const [stats, setStats] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState("");
  const [paymentDetails, setPaymentDetails] = useState("");
  const [paymentSaving, setPaymentSaving] = useState(false);
  const [payoutAmount, setPayoutAmount] = useState("");
  const [payoutRequesting, setPayoutRequesting] = useState(false);
  const [payoutDialog, setPayoutDialog] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [payoutsRes, statsRes] = await Promise.all([
        api.get("/payouts"),
        api.get("/dashboard/stats"),
      ]);
      setPayouts(payoutsRes.data);
      setStats(statsRes.data);
      setPaymentMethod(statsRes.data.user?.payment_method || "");
      setPaymentDetails(statsRes.data.user?.payment_details || "");
    } catch (err) { console.error(err); }
  };

  const savePaymentInfo = async () => {
    if (!paymentMethod || !paymentDetails) {
      toast.error("Please select a payment method and provide details");
      return;
    }
    setPaymentSaving(true);
    try {
      await api.put("/ambassador/payment-info", { payment_method: paymentMethod, payment_details: paymentDetails });
      toast.success("Payment info saved!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save");
    } finally {
      setPaymentSaving(false);
    }
  };

  const requestPayout = async () => {
    const amount = parseFloat(payoutAmount);
    if (!amount || amount < 10) {
      toast.error("Minimum payout amount is $10.00");
      return;
    }
    setPayoutRequesting(true);
    try {
      await api.post("/ambassador/request-payout", { amount });
      toast.success("Payout requested!");
      setPayoutDialog(false);
      setPayoutAmount("");
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to request payout");
    } finally {
      setPayoutRequesting(false);
    }
  };

  const statusStyles = {
    pending: "bg-yellow-500/10 text-yellow-400 border-yellow-500/20",
    processing: "bg-blue-500/10 text-blue-400 border-blue-500/20",
    completed: "bg-green-500/10 text-green-400 border-green-500/20",
  };

  const totalEarnings = stats?.earnings?.total_earnings || user?.total_earnings || 0;
  const availableBalance = stats?.available_balance ?? totalEarnings;
  const pendingTotal = payouts.filter(p => p.status === "pending" || p.status === "processing").reduce((s, p) => s + (p.amount || 0), 0);
  const hasPaymentInfo = paymentMethod && paymentDetails;

  return (
    <div className="space-y-6 max-w-5xl" data-testid="payouts-page">
      <div className="animate-fade-in-up">
        <h1 className="text-2xl sm:text-3xl font-semibold text-white/90 tracking-tight">Payouts</h1>
        <p className="text-white/35 text-sm mt-1">Manage your payment info and request payouts</p>
      </div>

      {/* Balance Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 stagger">
        <div className="glass glass-hover rounded-2xl p-5 animate-fade-in-up">
          <div className="w-9 h-9 rounded-xl bg-green-500/10 flex items-center justify-center mb-3">
            <DollarSign className="w-[18px] h-[18px] text-green-400" strokeWidth={1.5} />
          </div>
          <p className="font-mono text-3xl font-semibold text-green-400" data-testid="total-earnings">${totalEarnings.toFixed(2)}</p>
          <p className="text-xs text-white/30 mt-1">Total Earnings</p>
        </div>
        <div className="glass glass-hover rounded-2xl p-5 animate-fade-in-up">
          <div className="w-9 h-9 rounded-xl bg-[#F7941D]/10 flex items-center justify-center mb-3">
            <Wallet className="w-[18px] h-[18px] text-[#F7941D]" strokeWidth={1.5} />
          </div>
          <p className="font-mono text-3xl font-semibold text-[#F7941D]" data-testid="available-balance">${availableBalance.toFixed(2)}</p>
          <p className="text-xs text-white/30 mt-1">Available Balance</p>
        </div>
        <div className="glass glass-hover rounded-2xl p-5 animate-fade-in-up">
          <div className="w-9 h-9 rounded-xl bg-yellow-500/10 flex items-center justify-center mb-3">
            <TrendingUp className="w-[18px] h-[18px] text-yellow-400" strokeWidth={1.5} />
          </div>
          <p className="font-mono text-3xl font-semibold text-yellow-400" data-testid="pending-payouts">${pendingTotal.toFixed(2)}</p>
          <p className="text-xs text-white/30 mt-1">Pending Payouts</p>
        </div>
      </div>

      {/* Payment Info + Request Payout */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Payment Info */}
        <div className="glass rounded-2xl p-5 animate-fade-in-up" style={{ animationDelay: '120ms' }} data-testid="payment-info-card">
          <div className="flex items-center gap-2 mb-4">
            <CreditCard className="w-4 h-4 text-[#F7941D]" />
            <h3 className="text-base font-semibold text-white/85">Payment Information</h3>
          </div>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label className="text-white/50 text-sm">Payment Method</Label>
              <Select value={paymentMethod} onValueChange={setPaymentMethod}>
                <SelectTrigger className="glass-input rounded-xl h-11 text-sm" data-testid="payment-method-select">
                  <SelectValue placeholder="Select method" />
                </SelectTrigger>
                <SelectContent className="glass-strong rounded-xl border-white/[0.08]">
                  <SelectItem value="bank_account">
                    <span className="flex items-center gap-2"><Building2 className="w-3.5 h-3.5" /> Bank Account</span>
                  </SelectItem>
                  <SelectItem value="e_transfer">
                    <span className="flex items-center gap-2"><ArrowRightLeft className="w-3.5 h-3.5" /> E-Transfer</span>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-white/50 text-sm">
                {paymentMethod === "bank_account" ? "Bank Account Details (Institution, Transit, Account #)" :
                 paymentMethod === "e_transfer" ? "E-Transfer Email" :
                 "Payment Details"}
              </Label>
              <Input
                value={paymentDetails}
                onChange={e => setPaymentDetails(e.target.value)}
                placeholder={paymentMethod === "bank_account" ? "e.g., TD Bank / 12345 / 1234567" : paymentMethod === "e_transfer" ? "your@email.com" : "Select a payment method first"}
                className="glass-input rounded-xl h-11 text-sm"
                data-testid="payment-details-input"
              />
            </div>
            <button
              onClick={savePaymentInfo}
              disabled={paymentSaving || !paymentMethod || !paymentDetails}
              className="w-full bg-white/[0.06] hover:bg-white/[0.1] text-white/70 hover:text-white/90 font-medium rounded-full h-11 text-sm transition-all duration-300 border border-white/[0.08] disabled:opacity-40"
              data-testid="save-payment-info-button"
            >
              {paymentSaving ? "Saving..." : "Save Payment Info"}
            </button>
          </div>
        </div>

        {/* Request Payout */}
        <div className="glass rounded-2xl p-5 animate-fade-in-up" style={{ animationDelay: '180ms' }} data-testid="request-payout-card">
          <div className="flex items-center gap-2 mb-4">
            <Send className="w-4 h-4 text-[#F7941D]" />
            <h3 className="text-base font-semibold text-white/85">Request Payout</h3>
          </div>
          <div className="space-y-4">
            <div className="glass rounded-xl p-4 space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-white/40">Available</span>
                <span className="font-mono text-[#F7941D] font-medium">${availableBalance.toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-white/40">Minimum</span>
                <span className="font-mono text-white/50">$10.00</span>
              </div>
              {hasPaymentInfo && (
                <div className="flex justify-between text-sm">
                  <span className="text-white/40">Method</span>
                  <span className="text-white/60">{paymentMethod === "bank_account" ? "Bank Account" : "E-Transfer"}</span>
                </div>
              )}
            </div>

            {!hasPaymentInfo ? (
              <div className="text-center py-4">
                <p className="text-white/35 text-sm">Set up your payment information first to request payouts.</p>
              </div>
            ) : availableBalance < 10 ? (
              <div className="text-center py-4">
                <p className="text-white/35 text-sm">You need at least $10.00 to request a payout. Keep sharing your link!</p>
              </div>
            ) : (
              <Dialog open={payoutDialog} onOpenChange={setPayoutDialog}>
                <DialogTrigger asChild>
                  <button className="w-full bg-[#F7941D] hover:bg-[#ff9f2e] text-black font-semibold rounded-full h-11 text-sm transition-all duration-300 hover:shadow-[0_0_20px_rgba(247,148,29,0.3)]" data-testid="request-payout-button">
                    Request Payout
                  </button>
                </DialogTrigger>
                <DialogContent className="glass-strong rounded-2xl border-white/[0.08]">
                  <DialogHeader><DialogTitle className="text-white/90">Request Payout</DialogTitle></DialogHeader>
                  <div className="space-y-4 mt-2">
                    <div className="glass rounded-xl p-3 text-sm">
                      <span className="text-white/40">Available: </span>
                      <span className="font-mono text-[#F7941D] font-medium">${availableBalance.toFixed(2)}</span>
                    </div>
                    <div className="space-y-2">
                      <Label className="text-white/60 text-sm font-medium">Amount ($)</Label>
                      <Input
                        type="number"
                        min="10"
                        max={availableBalance}
                        step="0.01"
                        value={payoutAmount}
                        onChange={e => setPayoutAmount(e.target.value)}
                        placeholder="Min $10.00"
                        className="glass-input rounded-xl h-11 text-sm"
                        data-testid="payout-amount-input"
                      />
                    </div>
                    <button
                      onClick={requestPayout}
                      disabled={payoutRequesting}
                      className="w-full bg-[#F7941D] hover:bg-[#ff9f2e] text-black font-semibold rounded-full h-11 text-sm transition-all duration-300 disabled:opacity-50"
                      data-testid="confirm-payout-button"
                    >
                      {payoutRequesting ? "Requesting..." : "Confirm Payout Request"}
                    </button>
                  </div>
                </DialogContent>
              </Dialog>
            )}
          </div>
        </div>
      </div>

      {/* Payment History */}
      <div className="glass rounded-2xl overflow-hidden animate-fade-in-up" style={{ animationDelay: '240ms' }}>
        <div className="p-5 pb-3">
          <h3 className="text-base font-semibold text-white/85">Payment History</h3>
        </div>
        <div className="px-5 pb-5">
          {payouts.length === 0 ? (
            <p className="text-white/25 text-sm py-8 text-center">No payouts yet. Keep earning through referrals and tasks!</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-white/[0.06]">
                    <th className="text-left text-white/35 text-xs font-medium py-3 pr-4">Date</th>
                    <th className="text-left text-white/35 text-xs font-medium py-3 pr-4">Method</th>
                    <th className="text-right text-white/35 text-xs font-medium py-3 pr-4">Amount</th>
                    <th className="text-right text-white/35 text-xs font-medium py-3">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {payouts.map((p, i) => (
                    <tr key={i} className="border-b border-white/[0.03] last:border-0">
                      <td className="font-mono text-xs text-white/35 py-3 pr-4">{new Date(p.created_at).toLocaleDateString()}</td>
                      <td className="text-sm text-white/60 py-3 pr-4">{p.method}</td>
                      <td className="font-mono text-sm text-right text-green-400 font-medium py-3 pr-4">${(p.amount || 0).toFixed(2)}</td>
                      <td className="text-right py-3">
                        <Badge className={`${statusStyles[p.status] || ""} text-xs`} variant="outline">{p.status}</Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
